import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NarrowContainer } from "shared/UI/LayoutComponents/LayoutComponents";
import Stepper from "shared/UI/Stepper/Stepper";
import Button from "shared/UI/Buttons/Button";
import { useStatusModal } from "shared/core/hooks/useStatusModal";
import { trackEvents } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";
import { useMobilePlatform } from "core/hooks/useMobilePlatform";
import { PageHeader } from "UIPalette/PageHeader/PageHeader";
import PageTitle from "UIPalette/PageTitle/PageTitle";
import WaveBackground from "UIPalette/WaveBackground/WaveBackground";
import { ReactComponent as CloseCircle } from "@icons/close-circle.svg";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import { FeelingResources, useLogSupportMomentResourceUsedFunctionRequest, useLogReportingEventFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { AppRoutes } from "core/AppRoutes";
import {
  CHECK_IN_OPTION_CONFIG,
  CheckInOutcome,
  NOT_SURE_ROUTING_FEELING_MAP,
  NotSureRoutingOption,
  POSITIVE_MOMENT_STAGES,
  SUPPORT_OPTION_FEELING_MAP,
  SupportOption,
  getFeelingForResource,
  getResourceFilterForFeeling,
  getResourcesForFeelings,
  getSupportMomentsStage,
} from "./enums/supportMoments/supportMoments";
import { RecommendedResource } from "./Components/RecommendedResource/RecommendedResource";
import { NotSureRoutingOptions } from "./Components/NotSureRoutingOptions/NotSureRoutingOptions";
import { CheckInScreen } from "./Components/CheckInScreen/CheckInScreen";
import { PositiveOutcomeScreen } from "./Components/PositiveOutcomeScreen/PositiveOutcomeScreen";
import { NeutralOutcomeScreen } from "./Components/NeutralOutcomeScreen/NeutralOutcomeScreen";
import { NegativeOutcomeScreen } from "./Components/NegativeOutcomeScreen/NegativeOutcomeScreen";
import { useNavBarContext } from "core/state/NavBarContext/NavBarContext";
import { ReportingEventType } from "shared/constants/ReportingEventType";
import { useAppReviewContext } from "core/state/AppReviewContext/AppReviewContext";

type SupportMomentsLocationState = {
  option?: SupportOption;
  resources?: FeelingResources[];
};

export const SupportMoments = () => {
  const { t: tPageTitle } = useTranslation();
  const { t } = useTranslation(undefined, { keyPrefix: "supportMoments" });
  const navigate = useNavigate();
  const location = useLocation();
  const { setBody, Modal } = useStatusModal("leave-support-moments");
  const { option, resources } = (location.state ?? {}) as SupportMomentsLocationState;
  const [step, setStep] = useState(1);
  const [routingOption, setRoutingOption] = useState<NotSureRoutingOption | null>(null);
  const [resourceIndex, setResourceIndex] = useState(0);
  const [outcome, setOutcome] = useState<CheckInOutcome | null>(null);
  const xClientCode = ClientInfoState.getCode();
  const { trigger: triggerLogResourceUsed } = useLogSupportMomentResourceUsedFunctionRequest({ xClientCode });
  const { trigger: triggerLogReportingEvent } = useLogReportingEventFunctionRequest();

  useEffect(() => {
    triggerLogReportingEvent({
      body: {
        reportingEventType: ReportingEventType.SupportMomentOpened,
      },
    });
  }, [triggerLogReportingEvent]);

  const isNotSureJourney = option === SupportOption.NotSure;
  const totalSteps = isNotSureJourney ? 3 : 2;
  const { isMobileApp, insetPadding } = useMobilePlatform();

  const directFeeling = option ? SUPPORT_OPTION_FEELING_MAP[option] : undefined;
  const feelings = routingOption ? NOT_SURE_ROUTING_FEELING_MAP[routingOption] : directFeeling ? [directFeeling] : [];
  const resolvedResources = getResourcesForFeelings(resources, feelings);
  const resource = resolvedResources?.[resourceIndex];
  const resourceFilter = directFeeling ? getResourceFilterForFeeling(directFeeling) : undefined;
  const stage = getSupportMomentsStage({ option, outcome, step, hasResource: !!resource });
  const { handlePositiveMoment } = useAppReviewContext();

  const countLeavingPositiveMoment = useCallback(
    (delayExperienceModal = false) => {
      if (POSITIVE_MOMENT_STAGES.includes(stage)) {
        handlePositiveMoment(delayExperienceModal);
      }
    },
    [handlePositiveMoment, stage]
  );

  const handleFinish = useCallback(() => {
    triggerLogReportingEvent({
      body: {
        reportingEventType: ReportingEventType.SupportMomentCompleted,
        additionalInfo: option,
      },
    });

    trackEvents(EventNames.SUPPORT_MOMENT_LEAVE, { stage });
    navigate(AppRoutes.wellbeing, { replace: true });
    countLeavingPositiveMoment();
  }, [countLeavingPositiveMoment, navigate, option, stage, triggerLogReportingEvent]);

  const handleAttemptExit = useCallback(() => {
    setBody({
      data: {
        title: t("recommendedResource.leaveConfirmation.title"),
        description: t("recommendedResource.leaveConfirmation.message"),
        cta: t("recommendedResource.leaveConfirmation.leave"),
        cancelCta: t("recommendedResource.leaveConfirmation.back"),
      },
      status: "error",
      callback: handleFinish,
      cancelCallback: () => setBody(null),
    });
  }, [handleFinish, setBody, t]);

  const handleBack = useCallback(() => {
    if (step === 1) {
      handleAttemptExit();
      return;
    }

    setStep((prev) => prev - 1);
  }, [handleAttemptExit, step]);

  const handleNext = () => setStep((prev) => prev + 1);

  useNavBarContext({ callback: handleBack });

  const handleSelectRoutingOption = (selectedRoutingOption: NotSureRoutingOption) => {
    setRoutingOption(selectedRoutingOption);
    handleNext();
  };

  const handleCheckInFinish = (selectedOption: CheckInOutcome) => {
    const feeling = getFeelingForResource(resources, feelings, resource?.id);
    if (resource && feeling) {
      triggerLogResourceUsed({
        body: {
          feeling,
          resourceDatoId: resource.id,
          effect: CHECK_IN_OPTION_CONFIG[selectedOption].effect,
          firstInstance: resourceIndex === 0,
        },
      });

      triggerLogReportingEvent({
        body: {
          reportingEventType: ReportingEventType.SupportMomentOutcomeCheckIn,
          additionalInfo: `${option}|${resource.id}|${selectedOption}`,
        },
      });
    }
    
    if (selectedOption === CheckInOutcome.Same && resourceIndex > 0) {
      setOutcome(CheckInOutcome.Worse);
      return;
    }
    setOutcome(selectedOption);
  };

  const handleTryAnotherResource = () => {
    handlePositiveMoment(true);
    setResourceIndex(1);
    setOutcome(null);
    setStep(isNotSureJourney ? 2 : 1);
  };

  const renderOutcomeScreen = (outcome: CheckInOutcome) => {
    if (outcome === CheckInOutcome.Better) {
      return <PositiveOutcomeScreen option={option} resourceFilter={resourceFilter} onFinish={handleFinish} onLeave={countLeavingPositiveMoment} />;
    }

    if (outcome === CheckInOutcome.Same) {
      return (
        <NeutralOutcomeScreen
          option={option}
          resourceFilter={resourceFilter}
          onTryAnotherResource={handleTryAnotherResource}
          onFinish={handleFinish}
          onLeave={countLeavingPositiveMoment}
        />
      );
    }

    return <NegativeOutcomeScreen option={option} resourceFilter={resourceFilter} onFinish={handleFinish} />;
  };

  const renderStep1 = () => {
    if (isNotSureJourney) return <NotSureRoutingOptions onSelect={handleSelectRoutingOption} />;
    return <RecommendedResource resource={resource} onNotFoundCtaClick={handleFinish} onDone={handleNext} />;
  };

  const renderStep2 = () => {
    if (isNotSureJourney) return <RecommendedResource resource={resource} onNotFoundCtaClick={handleFinish} onDone={handleNext} />;
    return <CheckInScreen onFinish={handleCheckInFinish} />;
  };

  const renderStep3 = () => {
    if (isNotSureJourney) return <CheckInScreen onFinish={handleCheckInFinish} />;
    return null;
  };

  const pageTitle = <PageTitle title={tPageTitle("pageTitle.wellbeing.supportMoments")} />;

  if (outcome)
    return (
      <>
        {pageTitle}
        {renderOutcomeScreen(outcome)}
      </>
    );

  return (
    <>
      {pageTitle}
      <PageHeader
        hideForMobile
        onClickBack={handleBack}
        onClickNext={handleAttemptExit}
        nextTitle={t("recommendedResource.finish")}
        nextIcon={CloseCircle}
      />
      <WaveBackground>
        <NarrowContainer className="relative min-h-[466px]">
          {option ? (
            <>
              <div className="space-y-6">
                <Stepper steps={totalSteps} activeStep={step - 1} />

                {step === 1 && renderStep1()}

                {step === 2 && renderStep2()}

                {step === 3 && renderStep3()}
              </div>
              {isMobileApp && (
                <Button
                  className="fixed left-0 bottom-4 w-full"
                  style={insetPadding ? { bottom: `${insetPadding.bottom + 16}px` } : undefined}
                  text={t("recommendedResource.finish")}
                  variant="text"
                  onClick={handleAttemptExit}
                />
              )}
            </>
          ) : (
            <div className="text-center space-y-4">
              <p>{t("generalError.message")}</p>
              <Button text={t("generalError.cta")} onClick={handleFinish} />
            </div>
          )}
        </NarrowContainer>
      </WaveBackground>
      {Modal}
    </>
  );
};
